import { gql } from '@apollo/client'
import {
  ArrowRightIcon,
  BodyText,
  Button,
  ChevronRightIcon,
  Heading,
  TruncateText,
  useToast,
} from '@farol-ds/react'
import {
  CopyContentTriggerStandaloneFarol,
  CopyRequireLoginTrigger,
} from '@jusbrasil-web/shared-feature-locker-farol'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  COMPONENT_NAME,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4ClickEvent,
  GA4CustomComponentRenderedEvent,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { ListBlock } from '@jusbrasil-web/shared-list-block'
import { MouseEvent, useEffect, useMemo } from 'react'

import { ARTIFACT_LABEL, BOX_MODAL_IFRAME_URL, FEATURE_CODES } from '../../../constants'
import { useMetrics } from '../../../hooks'
import { DocPopoverJurisprudenceQuery } from './__generated__/doc-popover-jurisprudence.graphql'
import styles from './styles.module.scss'

export type DocPopoverJurisprudenceProps = {
  query: DocPopoverJurisprudenceQuery
  context: string
}

export function DocPopoverJurisprudence(props: DocPopoverJurisprudenceProps) {
  const {
    query: {
      root: { documents, viewer },
    },
    context,
  } = props

  const toast = useToast()
  const {
    ref: dispatcherRef,
    metricsProperties: defaultMetricsProperties,
    sendCopyEvents,
    sendMetrics,
  } = useMetrics<HTMLDivElement>()

  const { sendGA4Event } = useGA4Dispatcher()
  const ga4Props = {
    componentClass: COMPONENT_CLASS.RELATED_DOCUMENT_POPOVER,
    componentName: COMPONENT_NAME.RELATED_JURISPRUDENCE,
  }

  // caught by the error boundary
  if (!documents?.length) throw new Error('Documents not found')

  const isInTopicContext = context === 'TOPIC'
  const sourceComponent = isInTopicContext ? 'RelatedMandatoryPrecedent' : ''

  const defaultEventProps = useMemo(() => {
    const eventProps = {
      from_component: 'JurisPopoverLegis',
      source_component: sourceComponent,
      topic_type: isInTopicContext ? 'ATO_NORMATIVO' : '',
      metadata: {
        popover: {
          entity: 'DOCUMENTO',
          artifact: ARTIFACT_LABEL.JURISPRUDENCIA,
        },
      },
    }

    if (documents.length > 1) {
      return {
        ...eventProps,
        metadata: {
          popover: {
            ...eventProps.metadata.popover,
            docIds: documents,
          },
        },
      }
    }

    return {
      ...eventProps,
      metadata: {
        popover: {
          ...eventProps.metadata.popover,
          docId: documents[0]?.docId,
        },
      },
    }
  }, [documents, sourceComponent, isInTopicContext])

  useEffect(() => {
    sendGA4Event(
      new GA4CustomComponentRenderedEvent({
        ...ga4Props,
      })
    )
    const eventName = isInTopicContext ? 'Topic.PopoverOpened' : 'Juris.PopoverOpened'
    sendMetrics(eventName, defaultEventProps)
    // eslint-disable-next-line
  }, [])

  const onAnchorClick = (event: MouseEvent<HTMLAnchorElement>, docId?: number) => {
    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.RELATED_DOCUMENT_POPOVER,
        componentName: COMPONENT_NAME.RELATED_JURISPRUDENCE,
        selectedOption: SELECTED_OPTION.FULL_DOCUMENT_ACCESS,
        resourceId: `JURISPRUDENCIA:${docId}`,
      })
    )
    sendMetrics('Juris.PopoverAnchorClicked', {
      ...defaultEventProps,
      metadata: {
        popover: {
          ...defaultEventProps.metadata.popover,
          linkTrigger: event.currentTarget.parentElement?.innerHTML,
          clickOptions: documents,
          docId,
        },
      },
    })
  }

  if (documents.length > 1) {
    return (
      <div className={styles.contentRootSlim} ref={dispatcherRef}>
        <Heading size="sm">Esse termo pode se referir a:</Heading>

        <ListBlock>
          {documents.map((doc, idx) => (
            <ListBlock.Item key={idx}>
              <ListBlock.Content asChild>
                <a
                  href={doc?.url ?? ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => onAnchorClick(event, doc?.docId)}
                >
                  <ListBlock.Text>{doc?.title}</ListBlock.Text>
                  <ListBlock.Trailing>
                    <ChevronRightIcon />
                  </ListBlock.Trailing>
                </a>
              </ListBlock.Content>
            </ListBlock.Item>
          ))}
        </ListBlock>
      </div>
    )
  }

  const document = documents[0]
  const showMandatoryPrecedentLayout =
    document?.isPrecedenteObrigatorio && Boolean(document?.thesisText?.trim())

  // caught by the error boundary
  if (!document) throw new Error('Document not found')

  const metricsProperties = {
    ...defaultMetricsProperties,
    from_component: 'docview/DocPopoverJurisprudence',
  }
  const featureLockProps = {
    appId: 'web-docview',
    documentOrHaystackItem: document,
    featureCode: FEATURE_CODES.COPY_JURIS_CONTENT,
    ssrEventsProperties: metricsProperties,
    viewer,
  }

  const onCopy = () => {
    sendCopyEvents(metricsProperties)
    sendGA4Event(
      new GA4FeatureUseEvent({
        ...ga4Props,
        featureClass: FEATURE_CLASS.COPY_CONTENT,
        featureName: FEATURE_NAME.COPY_JURISPRUDENCE_CONTENT,
        resourceId: `JURISPRUDENCIA:${document.docId}`,
        requiredUserStatus: REQUIRED_USER_STATUS.AUTHENTICATED_USER,
      })
    )
  }

  const DocumentTitle = () => (
    <Heading size="md" className={styles.jurisHeading}>
      {document.title}
    </Heading>
  )

  const DecisionFacts = ({ withTruncate = false }: { withTruncate?: boolean }) => {
    const factsContent = (
      <div>
        {document.decisionFacts?.header && <p>{removeHtml(document.decisionFacts.header)}</p>}
        {document.decisionFacts?.paragraphs
          .filter((p): p is string => typeof p === 'string')
          .map((p, idx) => (
            <p key={idx}>{removeHtml(p)}</p>
          ))}
      </div>
    )

    return (
      <BodyText size="md" className={styles.jurisBody} asChild>
        {withTruncate ? (
          <TruncateText value={4} fade="md" defaultFadeVisible>
            {factsContent}
          </TruncateText>
        ) : (
          factsContent
        )}
      </BodyText>
    )
  }

  const ThesisSection = () => (
    <div>
      <Heading size="sm" asChild>
        <h2>Tese</h2>
      </Heading>
      <BodyText size="md" className={styles.jurisBody} asChild>
        <TruncateText value={7} fade="md">
          <p>{removeHtml(document.thesisText ?? '')}</p>
        </TruncateText>
      </BodyText>
    </div>
  )

  const DocumentContent = () => (
    <div
      onCopy={onCopy}
      ref={dispatcherRef}
      className={showMandatoryPrecedentLayout ? styles.contentRoot : undefined}
    >
      {showMandatoryPrecedentLayout ? (
        <div>
          <DocumentTitle />
          <DecisionFacts withTruncate />
        </div>
      ) : (
        <>
          <DocumentTitle />
          <DecisionFacts />
        </>
      )}
      {showMandatoryPrecedentLayout && <ThesisSection />}
    </div>
  )

  const CopyWrappedContent = () => (
    <CopyRequireLoginTrigger {...featureLockProps}>
      <CopyContentTriggerStandaloneFarol
        {...featureLockProps}
        eventNamespace="Juris"
        onCopySuccess={toast}
        iframeUrl={BOX_MODAL_IFRAME_URL}
      >
        <DocumentContent />
      </CopyContentTriggerStandaloneFarol>
    </CopyRequireLoginTrigger>
  )

  return (
    <div className={styles.contentRoot}>
      {showMandatoryPrecedentLayout ? (
        <CopyWrappedContent />
      ) : (
        <div className={styles.contentScroll}>
          <div className={styles.contentScrollArea}>
            <CopyWrappedContent />
          </div>
        </div>
      )}
      {document.url && (
        <div className={styles.contentFooter}>
          <Button rightIcon={<ArrowRightIcon />} asChild>
            <a
              href={document.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => onAnchorClick(event, document?.docId)}
            >
              Ir para jurisprudência
            </a>
          </Button>
        </div>
      )}
    </div>
  )
}

function removeHtml(str: string) {
  return str.replace(/<[^>]+>/gm, '')
}

DocPopoverJurisprudence.query = gql`
  query DocPopoverJurisprudence($artifact: String!, $ids: [NumericID!]!) {
    root {
      documents: documentsByNumericIDs(artifact: $artifact, ids: $ids) {
        docId
        artifact
        title
        url
        isPrecedenteObrigatorio
        thesisText
        decisionFacts {
          header
          paragraphs
        }
      }

      viewer: me {
        ...CopyRequireLoginTrigger_viewer
        ...CopyContentTriggerStandaloneFarol_viewer
      }
    }
  }

  ${CopyRequireLoginTrigger.fragments.viewer}
  ${CopyContentTriggerStandaloneFarol.fragments.viewer}
`
